// Classification morphologique déterministe à partir des mesures (cm).
//
// On produit une DISTRIBUTION douce sur les 6 morphologies (pas un verdict
// binaire) : ça alimente la jauge de certitude et déclenche les questions de
// confirmation seulement quand les ratios sont ambigus (proches d'une
// frontière).
//
// Interprétation des mesures (toutes en cm, en circonférence sauf épaules) :
//   - bust      : tour de poitrine (proxy du haut du corps)
//   - waist     : tour de taille
//   - hips      : tour de hanches (proxy du bas du corps)
//   - shoulders : largeur d'épaules (signal secondaire, optionnel)
//
// Tous les seuils sont des CONSTANTES réglables — à calibrer avec de vraies
// données clientes plus tard.

import { MORPHOLOGIES, type Morphology } from './types';

export interface Measurements {
  bust: number;
  waist: number;
  hips: number;
  shoulders?: number;
  /** Taille en cm (v3) — pour cohérence silhouette, pas utilisé pour le sizing. */
  height?: number;
}

// --- Seuils réglables ------------------------------------------------------

/** Écart poitrine/hanches (cm) à partir duquel on commence à pencher A ou V. */
const BALANCE_LO = 3;
/** Écart (cm) à partir duquel A ou V est franc (saturation). */
const BALANCE_HI = 9;
/** Ratio taille/hanches : en-dessous = taille très marquée (X / 8). */
const WAIST_MARKED_AT = 0.78;
/** Largeur de la rampe de "taille marquée" autour du seuil. */
const WAIST_MARKED_RAMP = 0.12;
/** Circonférence (cm) à partir de laquelle buste & hanches sont "volumineux" (→ 8). */
const VOLUME_PIVOT = 100;
const VOLUME_RAMP = 10;
/** À partir d'où la taille "déborde" → morphologie O. */
const BELLY_RAMP = 6;

/**
 * Calcule la distribution P(morphologie | mesures).
 * Somme normalisée à 1.
 */
export function classifyFromMeasurements(m: Measurements): Record<Morphology, number> {
  const B = m.bust;
  const W = m.waist;
  const H = m.hips;

  // 1) Rondeur centrale (O) : la taille s'approche ou dépasse le point le plus large.
  const widest = Math.max(B, H);
  const oNess = ramp(W - 0.95 * widest, 0, BELLY_RAMP);

  // 2) Déséquilibre haut/bas → A (hanches dominantes) ou V (poitrine dominante).
  let aNess = ramp(H - B, BALANCE_LO, BALANCE_HI);
  let vNess = ramp(B - H, BALANCE_LO, BALANCE_HI);

  // Affinage optionnel par la largeur d'épaules.
  if (typeof m.shoulders === 'number' && m.shoulders > 0) {
    // Épaules larges renforcent V ; épaules étroites renforcent A.
    // Référence neutre ~ hips/2.5 (heuristique douce).
    const ref = H / 2.5;
    const shoulderBias = clamp((m.shoulders - ref) / 6, -0.3, 0.3);
    vNess = clamp01(vNess + Math.max(shoulderBias, 0));
    aNess = clamp01(aNess + Math.max(-shoulderBias, 0));
  }

  const balanced = clamp01(1 - Math.max(aNess, vNess));

  // 3) Taille marquée ? (uniquement pertinent dans le cas équilibré)
  const waistRatio = W / H;
  const waistMarked = ramp(WAIST_MARKED_AT + WAIST_MARKED_RAMP / 2 - waistRatio, 0, WAIST_MARKED_RAMP);

  // 4) Volume généreux (buste ET hanches) → distingue 8 de X.
  const volumeHigh = ramp(Math.min(B, H) - (VOLUME_PIVOT - VOLUME_RAMP / 2), 0, VOLUME_RAMP);

  const notO = 1 - oNess;

  const scores: Record<Morphology, number> = {
    A: aNess * notO,
    V: vNess * notO,
    H: balanced * (1 - waistMarked) * notO,
    X: balanced * waistMarked * (1 - volumeHigh) * notO,
    '8': balanced * waistMarked * volumeHigh * notO,
    O: oNess,
  };

  return normalize(scores);
}

// --- Helpers ---------------------------------------------------------------

/** Rampe linéaire : 0 si x<=lo, 1 si x>=hi, interpolé entre. */
function ramp(x: number, lo: number, hi: number): number {
  if (hi <= lo) return x >= hi ? 1 : 0;
  return clamp01((x - lo) / (hi - lo));
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

function normalize(s: Record<Morphology, number>): Record<Morphology, number> {
  let sum = 0;
  for (const m of MORPHOLOGIES) sum += s[m];
  if (sum <= 1e-9) {
    // Aucun signal net : retombe sur l'uniforme.
    const u = {} as Record<Morphology, number>;
    for (const m of MORPHOLOGIES) u[m] = 1 / MORPHOLOGIES.length;
    return u;
  }
  const out = {} as Record<Morphology, number>;
  for (const m of MORPHOLOGIES) out[m] = s[m] / sum;
  return out;
}
